// @ts-nocheck
import React, {
  createContext,
  useContext,
  ReactChild,
  useEffect,
  useRef,
  useState,
} from 'react';
import RealtimeKitClient from '@cloudflare/realtimekit';

type Listener = () => void;
class RealtimeKitUpdates {
  private meeting: RealtimeKitClient;

  private l: Set<Listener>;

  constructor(meeting: RealtimeKitClient) {
    this.meeting = meeting;
    this.l = new Set();
    this.meeting.self?.addListener('*', this.update.bind(this));
    this.meeting.meta?.addListener('*', this.update.bind(this));
    this.meeting.participants?.addListener('*', this.update.bind(this));
    this.meeting.participants?.joined?.addListener('*', this.update.bind(this));
    this.meeting.participants?.active?.addListener('*', this.update.bind(this));
    this.meeting.participants?.pinned?.addListener('*', this.update.bind(this));
    this.meeting.chat?.addListener('*', this.update.bind(this));
    this.meeting.polls?.addListener('*', this.update.bind(this));
    this.meeting.plugins?.all?.addListener('*', this.update.bind(this));
    this.meeting.plugins?.active?.addListener('*', this.update.bind(this));
    this.meeting.recording?.addListener('*', this.update.bind(this));
    this.meeting.stage?.addListener('*', this.update.bind(this));
    this.meeting.livestream?.addListener('*', this.update.bind(this));
  }

  clean() {
    this.meeting.self?.removeListener('*', this.update.bind(this));
    this.meeting.meta?.removeListener('*', this.update.bind(this));
    this.meeting.participants?.removeListener('*', this.update.bind(this));
    this.meeting.participants?.joined?.removeListener(
      '*',
      this.update.bind(this)
    );
    this.meeting.participants?.active?.removeListener(
      '*',
      this.update.bind(this)
    );
    this.meeting.participants?.pinned?.removeListener(
      '*',
      this.update.bind(this)
    );
    this.meeting.chat?.removeListener('*', this.update.bind(this));
    this.meeting.polls?.removeListener('*', this.update.bind(this));
    this.meeting.plugins?.all?.removeListener('*', this.update.bind(this));
    this.meeting.plugins?.active?.removeListener('*', this.update.bind(this));
    this.meeting.recording?.removeListener('*', this.update.bind(this));
    this.meeting.stage?.removeListener('*', this.update.bind(this));
    this.meeting.livestream?.removeListener('*', this.update.bind(this));
  }

  addListener(listener: Listener) {
    this.l.add(listener);
  }

  removeListener(listener: Listener) {
    this.l.delete(listener);
  }

  private update() {
    this.l.forEach((l) => {
      l();
    });
  }
}

export const RealtimeKitContext = createContext<{
  meeting: RealtimeKitClient | undefined;
  updates: RealtimeKitUpdates | undefined;
}>({
  meeting: undefined,
  updates: undefined,
});

/**
 * Provider component which makes the RealtimeKitClient object
 * available to nested components
 * @component
 * @param value The RealtimeKitClient instance from `useRealtimeKitClient()`
 * @param renderBeforeLoad The provider will not render children by default
 * until it gets a client object, renderBeforeLoad can be used to override that
 */
export function RealtimeKitProvider({
  value,
  children,
  fallback = null,
}: {
  value: RealtimeKitClient | undefined;
  children: ReactChild | ReactChild[];
  fallback?: ReactChild | ReactChild[];
}) {
  const [updates, setUpdates] = useState<RealtimeKitUpdates>();
  const updatesRef = useRef<RealtimeKitUpdates>();

  useEffect(() => {
    if (value) {
      const newUpdate = new RealtimeKitUpdates(value);
      setUpdates(newUpdate);
      updatesRef.current = newUpdate;
    }
    return () => {
      updatesRef.current?.clean();
    };
  }, [value]);

  return (
    <RealtimeKitContext.Provider
      value={{
        meeting: value,
        updates,
      }}
    >
      {value ? children : fallback}
    </RealtimeKitContext.Provider>
  );
}

/**
 * Hook which returns the reference to the RealtimeKitClient object
 * @returns meeting instance from `useRealtimeKitClient()`
 */
export const useRealtimeKitMeeting = () => {
  const { meeting } = useContext(RealtimeKitContext);
  if (!meeting)
    throw new Error(
      'useRealtimeKitMeeting must be used within the RealtimeKitProvider'
    );

  return { meeting };
};
